import React, { useEffect, useState } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import css from './ApplePayCard.module.css';
const { REACT_APP_MARKETPLACE_ROOT_URL } = process.env;

const ApplePayButtonContainer = (props) => {
  const { customer, payinTotal, handleSubmitForm, submitDisabled } = props;
  const { amount, currency } = payinTotal || {};
  const { email, profile } = customer?.attributes || {};
  const { firstName, lastName, displayName, protectedData, publicData } = profile || {};
  const { customerId } = publicData || {};
  const {
    cards: savedCards,
    mobile_phone_number,
    whatsappPhoneNumber,
    whatsapp_phone_number,
    phoneNumber: protectedPhoneNumber,
  } =
    protectedData || {};


  const normalizePhoneValue = raw => {
    if (!raw) return { callingCode: undefined, phoneNumber: undefined };

    // Object forms we use in protectedData:
    // - { callingCode, phoneNumber }
    // - { countryCode, number }
    if (typeof raw === 'object') {
      const cc = raw.callingCode ?? raw.countryCode;
      const pn = raw.phoneNumber ?? raw.number;
      return { callingCode: cc != null ? String(cc) : undefined, phoneNumber: pn != null ? String(pn) : undefined };
    }

    // String form e.g. "+9665xxxxxxx" or "05xxxxxxx"
    if (typeof raw === 'string') {
      const digits = raw.replace(/[^0-9+]/g, '');
      const normalized = digits.startsWith('+') ? digits.slice(1) : digits;
      if (!normalized) return { callingCode: undefined, phoneNumber: undefined };

      // Saudi default parsing (project is SA-only in UI):
      // +9665xxxxxxxx => callingCode 966, phoneNumber 5xxxxxxxx
      // 05xxxxxxxx => callingCode 966, phoneNumber 5xxxxxxxx
      if (normalized.startsWith('966') && normalized.length > 3) {
        return { callingCode: '966', phoneNumber: normalized.slice(3) };
      }
      if (normalized.startsWith('0') && normalized.length >= 10) {
        return { callingCode: '966', phoneNumber: normalized.slice(1) };
      }
    }

    return { callingCode: undefined, phoneNumber: undefined };
  };

  const sanitizePhone = phone => {
    if (!phone?.callingCode || !phone?.phoneNumber) return { callingCode: undefined, phoneNumber: undefined };
    const cc = String(phone.callingCode).replace(/[^0-9]/g, '');

    // Keep only digits for the national number
    let pn = String(phone.phoneNumber).replace(/[^0-9]/g, '');

    // Remove leading 0 (e.g. 05xxxxxxxx)
    if (pn.startsWith('0')) pn = pn.slice(1);

    // If the number accidentally includes the calling code prefix, strip it.
    if (cc && pn.startsWith(cc)) pn = pn.slice(cc.length);

    // Saudi numbers should be 9 digits (typically 5xxxxxxxx)
    // If stored with extra digits, keep the last 9 digits.
    if (cc === '966' && pn.length > 9) {
      pn = pn.slice(-9);
    }

    return { callingCode: cc || undefined, phoneNumber: pn || undefined };
  };

  // Try multiple sources in order. Note: whatsapp_phone_number might be an object or string depending on data.
  const phoneCandidates = [mobile_phone_number, whatsappPhoneNumber, whatsapp_phone_number, protectedPhoneNumber];
  const normalizedCandidates = phoneCandidates.map(normalizePhoneValue).map(sanitizePhone);

  const firstValid = normalizedCandidates.find(p => p?.callingCode && p?.phoneNumber) || {
    callingCode: undefined,
    phoneNumber: undefined,
  };

  const callingCode = firstValid.callingCode;
  const phoneNumber = firstValid.phoneNumber;

  const isValidDigits = v => typeof v === 'string' && /^[0-9]+$/.test(v);
  const hasValidPhone = isValidDigits(callingCode || '') && isValidDigits(phoneNumber || '');

  // State to track if the device supports Apple Pay
  const [isApplePaySupported, setIsApplePaySupported] = useState(false);

  // Detect Apple device and Apple Pay support
  useEffect(() => {
    const isAppleDevice = () => {
      const userAgent = navigator.userAgent;
      const platform = navigator.platform;

      console.log('platform', platform, "and userAgent", userAgent);

      // Check for macOS or iOS devices
      return (
        /Mac|MacIntel|iPhone|iPad|iPod/.test(platform) ||
        /Macintosh|Mac|MacIntel|MacPPC|Mac68K|iPhone|iPad|iPod/.test(userAgent)
      );
    };

    const isSecure =
      typeof window !== 'undefined' &&
      (window.isSecureContext || window.location?.protocol === 'https:');

    let canMakeApplePayPayments = false;
    try {
      canMakeApplePayPayments =
        isSecure &&
        typeof window !== 'undefined' &&
        window.ApplePaySession &&
        typeof window.ApplePaySession.canMakePayments === 'function' &&
        window.ApplePaySession.canMakePayments();
    } catch (e) {
      console.warn('[ApplePay] canMakePayments failed (likely insecure context):', e);
      canMakeApplePayPayments = false;
    }

    if (isAppleDevice() && canMakeApplePayPayments) setIsApplePaySupported(true);
  }, []);

  // Load Apple Pay SDK and render button
  useEffect(() => {
    if (!isApplePaySupported) return;

    const renderApplePay = () => {
      const { render } = window.TapApplepaySDK || {};

      const hasRequiredFields =
        firstName && lastName && email && hasValidPhone && amount > 0 && !submitDisabled;

      if (render && hasRequiredFields) {
        const customerPayload = {
          id: customerId,
          name: [
            {
              locale: 'en',
              first: firstName,
              last: lastName,
              middle: '',
            },
          ],
          email: email,
        };

        // Only include phone when it is valid. Passing undefined often becomes the string "undefined"
        // inside downstream request builders.
        if (hasValidPhone) {
          customerPayload.phone = {
            countryCode: String(callingCode),
            number: String(phoneNumber),
          };
        }

        render(
          {
            debug: REACT_APP_MARKETPLACE_ROOT_URL === 'https://nowoffers.app' ? false : true,
            scope: 'TapToken',
            publicKey: process.env.REACT_APP_TAP_PUBLIC_KEY,
            environment:
              REACT_APP_MARKETPLACE_ROOT_URL === 'https://nowoffers.app' ? 'production' : 'development',
            merchant: {
              domain: REACT_APP_MARKETPLACE_ROOT_URL.replace(/^https?:\/\//, ''),
              id: process.env.REACT_APP_TAP_MERCHANT_ID,
            },
            acceptance: {
              supportedBrands: ['mada', 'masterCard', 'visa'],
            },
            features: {
              supportsCouponCode: false,
              shippingContactFields: ['name', 'phone', 'email'],
            },
            transaction: {
              amount: amount / 100,
              currency: 'SAR',
            },
            customer: customerPayload,
            interface: {
              locale: 'en',
              theme: 'dark',
              type: 'buy',
              edges: 'curved',
            },
            onCancel: () => console.log('onCancel'),
            onError: error => console.error('onError', error),
            onReady: test => console.log('onReady', test),
            onSuccess: async (data, event) => {
              console.log('onSuccess', data);
              console.log('event', event);
              handleSubmitForm({ data, formValues: {}, paymentMethod: 'applepay' });
            },
          },
          'apple-pay-button'
        );
      } else {
        console.warn('Apple Pay not rendered — missing required fields or form is disabled');
      }
    };

    // If SDK already loaded (e.g. user switched payment methods), just render.
    if (window.TapApplepaySDK && typeof window.TapApplepaySDK.render === 'function') {
      renderApplePay();
      return;
    }

    const existing = document.querySelector('script[src="https://tap-sdks.b-cdn.net/apple-pay/build-1.2.0/main.js"]');
    if (existing) {
      existing.addEventListener('load', renderApplePay);
      return () => existing.removeEventListener('load', renderApplePay);
    }

    const script = document.createElement('script');
    script.src = 'https://tap-sdks.b-cdn.net/apple-pay/build-1.2.0/main.js';
    script.setAttribute('data-tap-applepay-sdk', 'true');
    script.onload = renderApplePay;

    document.body.appendChild(script);

    return () => {
      if (script.parentNode) document.body.removeChild(script);
    };
  }, [
    isApplePaySupported,
    submitDisabled,
    firstName,
    lastName,
    email,
    callingCode,
    phoneNumber,
    amount,
  ]);

  // Load Apple Pay CSS
  useEffect(() => {
    if (!isApplePaySupported) return;

    const existing = document.querySelector('link[data-tap-applepay-css="true"]');
    if (existing) return;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://tap-sdks.b-cdn.net/apple-pay/build-1.2.0/main.css';
    link.setAttribute('data-tap-applepay-css', 'true');
    document.head.appendChild(link);

    return () => {
      if (link.parentNode) document.head.removeChild(link);
    };
  }, [isApplePaySupported]);

  // Render the button only if Apple Pay is supported
  return isApplePaySupported ? (
      <div id="apple-pay-button" className={css.applePayButton} />
  ) : null;
};

export default ApplePayButtonContainer;